import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import CustomTextField from "@components/inputs/customTextField";
import ConfirmDialog from "@components/dialog/confirmDialog";

import { useSnackbar } from "@context/snackbarContext";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";
import { useAuthentication } from "@redux/authentication/hook";

import { formatDate, formatDateTime } from "@utils/formatter";

import { ACTION } from "@utils/constant";
import CustomButton from "@components/button/customButton";

export default function InquiryForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user } = useAuthentication()
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view" || "edit"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");


  const [data, setData] = useState(null)

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  useEffect(()=>{
    if (!id) {
      onBack()
    }
    if (id) {
      fetchInquiryMutation.mutate(id)
    }
  },[id])

  const fetchInquiryMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getInquiry}/${id}`)
    },
    onSuccess: (res) => {
      const data = res.data
      // console.log(data)
      setData(data)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve inquiry", "error");
    }
  });

  const repliedInquiryMutation = useMutation({
    mutationFn: () => {
      return axios.put(`${AdminApiEndpoints.updateInquiryStatus}/${id}`, {
        statusGuid: "b472ee4f-496b-42a5-a2d3-14f3eaf1ca5f",
        by: user?.username,
      })
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getInquiry'] })
      openSnackbar("Successfully updated inquiry status", "success");
      fetchInquiryMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar("Failed to update inquiry status", "error");
    }
  })

  const onConfirmReplied = () => {
    repliedInquiryMutation.mutate()
  }

  const onBack = () => {
    navigate('/admin/inquiries', { replace: true });
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          (data && data.statusGuid !== "b472ee4f-496b-42a5-a2d3-14f3eaf1ca5f") &&
          <CustomButton onClick={()=>setOpenConfirmDialog(true)} color="#4DB6AC" loading={repliedInquiryMutation.isPending}>Replied</CustomButton>
        }
      />
      <SkeletonWrapper isLoading={fetchInquiryMutation.isPending} isError={fetchInquiryMutation.isError} errorMessage="Unable to load inquiry" disabled={["create"].includes(pageAction)}>
      <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Inquiry Details</Typography>
            </Grid2>
            <Grid2 size={12}>
              <CustomTextField 
                value={data?.fullName}
                label="Full Name"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.emailAddress}
                label="Email Address"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.contactNumber}
                label="Contact Number"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={data?.status?.valueEng}
                label="Status"
                readOnly
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <CustomTextField 
                value={formatDateTime(data?.createdOn)}
                label="Date & Time"
                readOnly
              />
            </Grid2>

            <Grid2 size={12}>
              <CustomTextField 
                value={data?.message}
                label="Inquiry"
                readOnly
                multiline
                rows={5}
              />
            </Grid2>

          </Grid2>
        </Paper>

      </div>
      </SkeletonWrapper>
      <ConfirmDialog 
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Change inquiry status to replied?"
        onSubmit={()=>onConfirmReplied()}
        loading={repliedInquiryMutation.isPending || repliedInquiryMutation.isPending}
      />
    </div>
  );
}
